import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export interface PayoutConfiguration {
  isConfigured: boolean;
  version?: number;
  channel?: 'momo' | 'bank';
  bankOrNetwork?: string;
  bankOrNetworkName?: string;
  accountOrPhone?: string;
  accountName?: string;
  feeBearer?: 'shared' | 'customer';
  schoolFeeRate?: number;
  customerFeeRate?: number;
  totalFeeRate?: number;
  settlementCode?: string;
  lockedAt?: string | null;
}

// What the counter needs to take a digital payment; readable by every
// payment-taking role. Settlement details stay on the admin-only config.
export interface PayoutStatus {
  isConfigured: boolean;
  feeBearer: 'shared' | 'customer' | null;
  customerFeeRate: string | null;
}

export interface BankChoice {
  name: string;
  code: string;
}

export interface ResolvePayoutRequest {
  channel: 'momo' | 'bank';
  bankOrNetwork: string;
  accountOrPhone: string;
}

export interface ResolvePayoutResponse {
  accountName: string;
  resolutionToken: string;
}

export interface CommitPayoutRequest {
  channel: 'momo' | 'bank';
  bankOrNetwork: string;
  accountOrPhone: string;
  resolutionToken: string;
  feeBearer: 'shared' | 'customer';
}

import type { CheckoutState } from '../../ParentApp/PaymentProcess/checkoutTypes';

export const payoutApi = createApi({
  reducerPath: 'payoutApi',
  baseQuery,
  tagTypes: ['PayoutConfig', 'PayoutStatus'],
  endpoints: builder => ({
    getPayoutConfig: builder.query<PayoutConfiguration, void>({
      query: () => '/payments/payout',
      providesTags: ['PayoutConfig'],
    }),
    getPayoutStatus: builder.query<PayoutStatus, void>({
      query: () => '/payments/payout/status',
      providesTags: ['PayoutStatus'],
    }),
    getBanksList: builder.query<BankChoice[], void>({
      query: () => '/payments/banks',
    }),
    resolvePayoutDestination: builder.mutation<
      ResolvePayoutResponse,
      ResolvePayoutRequest
    >({
      query: body => ({
        url: '/payments/payout/resolve',
        method: 'POST',
        body,
      }),
    }),
    commitPayoutDestination: builder.mutation<
      PayoutConfiguration,
      CommitPayoutRequest
    >({
      query: body => ({
        url: '/payments/payout',
        method: 'POST',
        body,
      }),
      // Patch the cache from the 201 body so the page flips to the locked
      // view immediately instead of waiting on the invalidation refetch.
      async onQueryStarted(_body, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.isConfigured) {
            dispatch(
              payoutApi.util.updateQueryData(
                'getPayoutConfig',
                undefined,
                () => data
              )
            );
          }
        } catch {
          // Failures are surfaced by the caller; nothing to patch.
        }
      },
      invalidatesTags: ['PayoutConfig', 'PayoutStatus'],
    }),
    getTransactionStatus: builder.query<CheckoutState, string>({
      query: reference => ({
        url: `/payments/transactions/${reference}/status`,
      }),
      keepUnusedDataFor: 0,
      // A person is waiting on "did my money move?": never held by the outage hold.
      extraOptions: { bypassOutageHold: true },
    }),
    confirmTransactionOtp: builder.mutation<
      CheckoutState,
      { reference: string; otp: string }
    >({
      query: ({ reference, otp }) => ({
        url: `/payments/transactions/${reference}/confirm-otp`,
        method: 'POST',
        body: { otp },
      }),
    }),
  }),
});

export const {
  useGetPayoutConfigQuery,
  useGetPayoutStatusQuery,
  useGetBanksListQuery,
  useResolvePayoutDestinationMutation,
  useCommitPayoutDestinationMutation,
  useLazyGetTransactionStatusQuery,
  useConfirmTransactionOtpMutation,
} = payoutApi;
